import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Card, Col, Divider, Form, Input, notification, Row, Space } from "antd";
import { RequiredMark } from "antd/lib/form/Form";
import TextArea from "antd/lib/input/TextArea";
import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router";
import { Link } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { getEnvironmentService } from "../../backend";
import { trackEnvRemoveFailed, trackEnvRemoveSucceeded } from "../../backend/tracking/environment";
import { useDoubleConfigurationModal } from "../../contexts/Modal";
import { currentProjectState } from "../../recoil/project";
import { AwsRegionType, IEnvDetails } from "../../types";

export default function EnvironmentSettingsGeneral() {
    const [envForm] = Form.useForm();
    const navigate = useNavigate();
    const { projectId, envId } = useParams();
    const { open: openConfirmModal } = useDoubleConfigurationModal();
    const currentProject = useRecoilValue(currentProjectState);
    const { envDetails } = useOutletContext<{ envDetails: IEnvDetails }>();
    const [requiredMark] = useState<RequiredMark>('optional');
    const [loadingUpdateEnv, setLoadingUpdateEnv] = useState(false);
    const [isProjectPaymentPlanFree, setIsProjectPaymentPlanFree] = useState(false);

    const [canDelete] = useState(
        ![
            "CREATE_COMPLETE",
            "UPDATE_COMPLETE",
            "ROLLBACK_COMPLETE",
            "UPDATE_ROLLBACK_COMPLETE",
            "CREATE_IN_PROGRESS",
            "DELETE_IN_PROGRESS",
            "ROLLBACK_IN_PROGRESS",
            "UPDATE_IN_PROGRESS",
            "UPDATE_COMPLETE_CLEANUP_IN_PROGRESS",
            "UPDATE_ROLLBACK_COMPLETE_CLEANUP_IN_PROGRESS",
            "UPDATE_ROLLBACK_IN_PROGRESS",
            "DELETE_FAILED"
        ].includes(envDetails.status || "")
    );

    useEffect(() => {
        setIsProjectPaymentPlanFree(!!(currentProject && currentProject.paymentPlan?.id === "FREE"));
    }, [currentProject]);

    async function handleUpdateEnvironment(values: { name: string, description: AwsRegionType }) {
        try {
            setLoadingUpdateEnv(true);
            await getEnvironmentService().updateStage(
                envId!,
                projectId!,
                {
                    name: values.name,
                    description: values.description
                }
            );
            notification.success({ message: `Environment '${values.name}' has been updated sucessfully.` });
        } catch (error: any) {
            notification.error({
                message: "Update environment defails failed",
                description: error.response.data.message
            });
        } finally {
            setLoadingUpdateEnv(false);
        }
    }

    async function handleDeleteEnv() {
        try {
            await getEnvironmentService().deleteStage(envId!, projectId!);
            trackEnvRemoveSucceeded(envDetails.id, envDetails.name);
            notification.success({ message: "The environment has been deleted succcessfully." });
            navigate(`/projects/${projectId!}`);
        } catch (error: any) {
            trackEnvRemoveFailed(envDetails.id, envDetails.name, error.response.data.message);
            notification.error({ message: error?.response?.data?.message || "We encountered an error while trying to delete this environment." });
        }
    }

    return (
        <Space direction="vertical" className="full-width">
            <Card bordered>
                <Card.Meta
                    title="General settings"
                    description="Details about your environment"
                />
                <Divider />
                <Form
                    form={envForm}
                    layout="vertical"
                    initialValues={{
                        name: envDetails.name,
                        description: envDetails.description,
                    }}
                    onValuesChange={() => { }}
                    requiredMark={requiredMark}
                    wrapperCol={{}}
                    onFinish={handleUpdateEnvironment}
                >
                    <Form.Item
                        name="name"
                        label="Name"
                        required
                        rules={[{ required: true, message: 'Please select cloud account!' }]}
                    >
                        <Input placeholder="DEV" style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="Description"
                        required
                        rules={[{ required: true, message: 'Please select cloud region!' }]}
                    >
                        <TextArea placeholder="My production environment" showCount maxLength={200} style={{ height: 100, marginBottom: "20px" }} />
                    </Form.Item>
                    <Divider />
                    <Row style={{
                        display: "flex",
                        alignItems: "center"
                    }}>
                        <Col span={24} style={{ textAlign: "right" }}>
                            <Button htmlType="submit" loading={loadingUpdateEnv}>
                                Save
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Card>
            <br />
            <Card bordered>
                <Card.Meta
                    title={
                        <Space>
                            <ExclamationCircleOutlined style={{ color: 'red' }} />
                            Delete Environment
                        </Space>
                    }
                    description="Completely remove the environment."
                />
                <Divider />
                {
                    isProjectPaymentPlanFree && <>
                        You are not able to delete your only environment when using a project on a Free plan.
                    </>
                }
                {
                    !canDelete && <>
                        <Link to={`/projects/${projectId}/environments/${envId}/settings/infrastructure`}>Undeploy Infrastructure</Link> to be able to remove this environment.
                    </>
                }
                <Row style={{
                    display: "flex",
                    "alignItems": "center"
                }}>
                    <Col span={24} style={{ textAlign: "right" }}>
                        <Button type="primary"
                            danger
                            disabled={!canDelete || isProjectPaymentPlanFree}
                            onClick={() => openConfirmModal({
                                title: "Delete Environment?",
                                description: "The environment will be permanently deleted, including its resources.",
                                alert: <b>This action cannot be undone.</b>,
                                confirmation: envId!,
                                okText: "Delete Environment",
                                cancelText: 'Cancel',
                                onOk: () => handleDeleteEnv()
                            })}
                        >
                            Delete
                        </Button>
                    </Col>
                </Row>
            </Card>
        </Space>
    );

}